import { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api-client';

export interface CoachProfileEditValues {
  name: string;
  bio: string;
  experience: string;
  trainingSpecialties: string[];
  hasGym: boolean;
  gymLocation: string;
  estimatedClients: number;
}

const EMPTY: CoachProfileEditValues = {
  name: '',
  bio: '',
  experience: '',
  trainingSpecialties: [],
  hasGym: false,
  gymLocation: '',
  estimatedClients: 0
};

interface CoachLike {
  name?: string | null;
  bio?: string | null;
  experience?: string | null;
  trainingSpecialties?: string[] | null;
  hasGym?: boolean | null;
  gymLocation?: string | null;
  estimatedClients?: number | null;
}

export interface UseCoachProfileEditDialogResult {
  visible: boolean;
  loading: boolean;
  values: CoachProfileEditValues;
  errors: Partial<Record<keyof CoachProfileEditValues, string>>;
  open: () => void;
  close: () => void;
  // eslint-disable-next-line no-unused-vars
  setField: <K extends keyof CoachProfileEditValues>(field: K, value: CoachProfileEditValues[K]) => void;
  save: () => Promise<{ success: boolean; message?: string }>;
}

/**
 * Estado local del modal `CoachProfileEditDialog`. Reemplaza al wizard
 * `CoachProfileForm` en Fase 2 del onboarding (registro simplificado).
 *
 * Diseño: todos los campos son opcionales en el backend, pero pedimos al
 * menos `name` para que el modal sirva como gate del checklist (tarea #1
 * "Completar perfil").
 */
export const useCoachProfileEditDialog = (initial?: CoachLike | null): UseCoachProfileEditDialogResult => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<CoachProfileEditValues>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof CoachProfileEditValues, string>>>({});

  // Hidratar al abrir cuando llegan datos del coach.
  useEffect(() => {
    if (!initial) return;
    setValues({
      name: initial.name ?? '',
      bio: initial.bio ?? '',
      experience: initial.experience ?? '',
      trainingSpecialties: initial.trainingSpecialties ?? [],
      hasGym: initial.hasGym ?? false,
      gymLocation: initial.gymLocation ?? '',
      estimatedClients: initial.estimatedClients ?? 0
    });
  }, [initial]);

  const open = useCallback((): void => {
    setVisible(true);
    setErrors({});
  }, []);

  const close = useCallback((): void => {
    setVisible(false);
  }, []);

  const setField = useCallback(
    <K extends keyof CoachProfileEditValues>(field: K, value: CoachProfileEditValues[K]): void => {
      setValues((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const save = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    const nextErrors: Partial<Record<keyof CoachProfileEditValues, string>> = {};
    if (!values.name || values.name.trim().length < 2) {
      nextErrors.name = 'El nombre debe tener al menos 2 caracteres';
    }
    if (values.hasGym && (!values.gymLocation || !values.gymLocation.trim())) {
      nextErrors.gymLocation = 'Indicá la ubicación del gimnasio';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return { success: false, message: 'Revisá los campos marcados.' };
    }

    setLoading(true);
    try {
      await api.coach.updateProfile({
        name: values.name.trim(),
        bio: values.bio.trim() || undefined,
        experience: values.experience.trim() || undefined,
        trainingSpecialties: values.trainingSpecialties,
        hasGym: values.hasGym,
        gymLocation: values.hasGym ? values.gymLocation.trim() : undefined,
        estimatedClients: values.estimatedClients
      });
      setVisible(false);
      return { success: true };
    } catch (error) {
      const msg = (error as Error).message ?? 'No se pudo guardar el perfil';
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, [values]);

  return { visible, loading, values, errors, open, close, setField, save };
};
